// Events Release B (#680): FORMATTING for the server's `checkoutWords`. The words are RESOLVED on
// the server (services/events/eventSettings.ts, `resolveCheckoutWords`); nothing here decides which
// wording an event uses, so there is no copy of that rule to keep in step.

import type { CheckoutWords } from '../api/events.api';

/** "ticket" -> "Ticket", "meal pass" -> "Meal Pass": for headings and buttons. */
export function titleCaseWords(words: string): string {
  return words.replace(/(^|[ -])(\p{L})/gu, (_m, sep: string, ch: string) => sep + ch.toUpperCase());
}

/** The word for a count: one is singular, anything else plural ("1 place", "0 places", "2 places"). */
export function countWord(words: CheckoutWords, count: number): string {
  return count === 1 ? words.singular : words.plural;
}

/**
 * The shipped defaults, for the Event Settings page's "Reset to default" only. This is a display
 * constant, NOT the resolver (which words an event uses stays server-side); the server falls back
 * to the same words when a stored value is missing or malformed.
 */
export const DEFAULT_CHECKOUT_WORDING: Record<'ticket' | 'place', CheckoutWords> = {
  ticket: { verb: 'Buy', singular: 'ticket', plural: 'tickets' },
  place: { verb: 'Book', singular: 'place', plural: 'places' },
};

/** Mirrors the server's limits (constants/eventSettings.constants.ts): the server is the authority. */
export const CHECKOUT_WORD_MAX_LENGTH = 20;
export const CHECKOUT_WORD_PATTERN = /^\p{L}[\p{L}\p{M} -]*$/u;

/** The server stores a word trimmed and NFC-composed; judge it the same way. */
export const normaliseCheckoutWord = (value: string): string => value.trim().normalize('NFC');

/** A verb opens a button or heading, so the server capitalises it; the preview does too. */
export const capitaliseFirst = (word: string): string => word.charAt(0).toLocaleUpperCase() + word.slice(1);

/** An error message for one typed word, or null when it is acceptable. */
export function checkoutWordError(value: string): string | null {
  const v = normaliseCheckoutWord(value);
  if (v.length === 0) return 'Enter a word';
  if (v.length > CHECKOUT_WORD_MAX_LENGTH) return `At most ${CHECKOUT_WORD_MAX_LENGTH} characters`;
  if (!CHECKOUT_WORD_PATTERN.test(v)) return 'Letters, spaces and hyphens only';
  return null;
}

/** The fallback used only while a page has no event yet (loading). The server is the real source. */
export const NEUTRAL_CHECKOUT_WORDS: CheckoutWords = { verb: 'Buy', singular: 'ticket', plural: 'tickets' };
